
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { SignOutButton } from "@/components/SignOutButton";
import {
  HomeIcon,
  GanttChartIcon,
  Receipt,
  UserRound,
  Users,
  BarChart3,
  Menu,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export function MobileOptimizedNavigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { session } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!session?.user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data } = await supabase
          .from('admin_users')
          .select('id')
          .eq('user_id', session.user.id)
          .maybeSingle();

        setIsAdmin(!!data);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [session]);

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (!session) return null;

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: HomeIcon },
    { path: "/projects", label: "Projects", icon: GanttChartIcon },
    { path: "/clients", label: "Clients", icon: Users },
    { path: "/invoices", label: "Invoices", icon: Receipt },
    { path: "/profile", label: "Profile", icon: UserRound },
  ];

  if (isAdmin) {
    navItems.push({ path: "/admin/dashboard", label: "Admin", icon: BarChart3 });
  }

  return (
    <>
      {/* Mobile-optimized header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b ios-safe-area-top">
        <div className="flex h-16 items-center justify-between px-4">
          <Link to="/" className="font-bold text-lg">
            Hour Tracker
          </Link>
          
          <div className="flex items-center gap-2">
            
            {/* Mobile menu trigger */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMenuOpen(!menuOpen)}
              className="mobile-touch-target"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-out menu */}
      <div
        className={cn(
          "fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out",
          menuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setMenuOpen(false)}
        />
        
        {/* Menu panel */}
        <div className="absolute right-0 top-0 h-full w-80 max-w-[80vw] bg-background shadow-xl ios-safe-area-top ios-safe-area-bottom">
          <div className="flex flex-col h-full">
            {/* Menu header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-semibold text-lg">Menu</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMenuOpen(false)}
                className="mobile-touch-target"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Navigation items */}
            <div className="flex-1 overflow-y-auto">
              <div className="py-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "mobile-nav-item hover:bg-muted transition-colors",
                      isActive(item.path) && "bg-muted text-primary font-medium"
                    )}
                    onClick={() => setMenuOpen(false)}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Sign out button */}
            <div className="p-4 border-t">
              <SignOutButton 
                onClick={() => setMenuOpen(false)}
                className="w-full mobile-button"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
